/**
 * SearchAiz Settings Resolver
 *
 * Implements the 3-layer settings inheritance model:
 * Portal (system) → Developer (app) → User (personal)
 *
 * Each layer can:
 * - Set default values
 * - Lock settings (prevent lower layers from changing)
 * - Hide settings (remove from UI)
 * - Allow modifications by next layer
 *
 * The resolver produces a final "resolved" settings object
 * that the UI components use.
 */

import type {
  SearchAizSettings,
  PartialSettings,
  LayeredSettings,
  LayerPermissions,
  SettingsLayer,
  SearchContext,
  SearchMode,
  DeepPartial,
} from '../types';

// =============================================================================
// DEFAULT SETTINGS
// =============================================================================

/**
 * System defaults - the baseline configuration
 */
export const DEFAULT_SETTINGS: SearchAizSettings = {
  version: 1,

  mode: {
    default: 'ai-assisted',
    allowUserChange: true,
    availableModes: ['simple', 'ai-assisted', 'conversational', 'full'],
  },

  ai: {
    rerank: {
      enabled: true,
      tier: 'standard',
      scoreThreshold: 0.3,
      userCanDisable: true,
    },
    analysis: {
      enabled: false,
      mode: 'structured',
      userCanToggle: true,
    },
    conversation: {
      enabled: false,
      maxFollowUps: 5,
      userCanToggle: false, // Developer decides if conversation is available
    },
  },

  sources: {
    available: ['knowledge_base', 'calls', 'entities', 'web', 'operations', 'application'],
    default: ['knowledge_base'],
    userCanChange: true,
    userCanSelectPerSearch: true,
  },

  results: {
    perPage: 10,
    maxResults: 100,
    candidatePoolSize: 50,
    grouping: 'none',
    sortDefault: 'relevance',
    showScores: false,
    showSources: true,
    showPreviews: true,
    previewMaxChars: 200,
  },

  ui: {
    displayMode: 'auto',
    showAdvancedOptions: false,
    showFilters: false,
    showStats: false,
    keyboardShortcuts: true,
    theme: 'system',
    accentColor: undefined,
  },

  contextOverrides: {
    header: {
      mode: { default: 'simple' },
      results: { perPage: 5 },
      ui: { displayMode: 'compact', showFilters: false },
    },
    fullPage: {
      mode: { default: 'ai-assisted' },
      results: { perPage: 20 },
      ui: { showFilters: true, showStats: true },
    },
    modal: {
      mode: { default: 'ai-assisted' },
      results: { perPage: 10 },
    },
    sidebar: {
      mode: { default: 'simple' },
      results: { perPage: 8 },
      ui: { displayMode: 'compact' },
    },
    embedded: {
      mode: { default: 'simple' },
      results: { perPage: 5 },
      ui: { displayMode: 'compact', showAdvancedOptions: false },
    },
  },

  permissions: {
    modifiable: [],
    locked: [],
    hidden: [],
  },
};

/**
 * Default permissions for each layer
 */
export const DEFAULT_PORTAL_PERMISSIONS: LayerPermissions = {
  // Portal allows developers to modify most settings
  modifiable: [
    'mode.default',
    'mode.availableModes',
    'ai.rerank.enabled',
    'ai.rerank.tier',
    'ai.analysis.enabled',
    'ai.analysis.mode',
    'ai.conversation.enabled',
    'sources.available',
    'sources.default',
    'results.perPage',
    'results.maxResults',
    'results.grouping',
    'results.showScores',
    'results.showSources',
    'ui.showAdvancedOptions',
    'ui.showFilters',
    'ui.showStats',
  ],
  // Portal can lock billing-sensitive settings
  locked: [],
  // Nothing hidden by default
  hidden: [],
};

export const DEFAULT_DEVELOPER_PERMISSIONS: LayerPermissions = {
  // Developers allow users to modify UI preferences
  modifiable: [
    'mode.default',
    'ai.rerank.enabled',
    'ai.analysis.enabled',
    'sources.default',
    'results.perPage',
    'results.showScores',
    'results.showSources',
    'results.showPreviews',
    'ui.displayMode',
    'ui.keyboardShortcuts',
    'ui.theme',
  ],
  locked: [],
  hidden: [],
};

// =============================================================================
// DEEP MERGE UTILITY
// =============================================================================

/**
 * Deep merge two objects, with source overwriting target
 */
function deepMerge<T>(
  target: T,
  source: DeepPartial<T>
): T {
  // Handle null/undefined cases
  if (source === null || source === undefined) {
    return target;
  }
  if (target === null || target === undefined) {
    return source as T;
  }

  // If target is not an object, just return source
  if (typeof target !== 'object' || Array.isArray(target)) {
    return (source as T) ?? target;
  }

  const result = { ...target } as T;
  const sourceObj = source as Record<string, unknown>;
  const targetObj = target as Record<string, unknown>;
  const resultObj = result as Record<string, unknown>;

  for (const key of Object.keys(sourceObj)) {
    const sourceValue = sourceObj[key];
    const targetValue = targetObj[key];

    if (sourceValue === undefined) {
      continue;
    }

    if (
      sourceValue !== null &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue !== null &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      // Recursively merge objects
      resultObj[key] = deepMerge(
        targetValue,
        sourceValue as DeepPartial<typeof targetValue>
      );
    } else {
      // Direct assignment for primitives and arrays
      resultObj[key] = sourceValue;
    }
  }

  return result;
}

/**
 * Get a nested value from an object using dot notation
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current, key) => {
    if (current && typeof current === 'object') {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj as unknown);
}

/**
 * Set a nested value in an object using dot notation
 */
function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void {
  const keys = path.split('.');
  const lastKey = keys.pop()!;

  let current = obj;
  for (const key of keys) {
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[lastKey] = value;
}

// =============================================================================
// SETTINGS RESOLVER
// =============================================================================

/**
 * Check if a setting path is allowed to be modified
 */
function canModify(
  path: string,
  higherLayerPermissions: LayerPermissions
): boolean {
  // If explicitly locked by higher layer, cannot modify
  if (higherLayerPermissions.locked.includes(path)) {
    return false;
  }

  // If not in modifiable list, cannot modify
  if (!higherLayerPermissions.modifiable.includes(path)) {
    return false;
  }

  return true;
}

/**
 * Apply a layer's settings, respecting the higher layer's permissions
 */
function applyLayerSettings(
  base: SearchAizSettings,
  layer: PartialSettings,
  higherPermissions: LayerPermissions
): SearchAizSettings {
  // Start with base
  let result = { ...base };

  // Apply each setting from the layer if allowed
  const flattenSettings = (
    obj: Record<string, unknown>,
    prefix = ''
  ): Array<[string, unknown]> => {
    const entries: Array<[string, unknown]> = [];

    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;

      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        entries.push(...flattenSettings(value as Record<string, unknown>, path));
      } else {
        entries.push([path, value]);
      }
    }

    return entries;
  };

  const layerEntries = flattenSettings(layer as Record<string, unknown>);

  for (const [path, value] of layerEntries) {
    // Skip special keys
    if (path === 'version' || path === 'permissions' || path.startsWith('contextOverrides')) {
      continue;
    }

    // Check if this setting can be modified
    if (canModify(path, higherPermissions)) {
      setNestedValue(result as unknown as Record<string, unknown>, path, value);
    }
  }

  // Handle context overrides separately (deep merge)
  if (layer.contextOverrides) {
    result.contextOverrides = deepMerge(
      result.contextOverrides,
      layer.contextOverrides as DeepPartial<SearchAizSettings['contextOverrides']>
    );
  }

  return result;
}

/**
 * Calculate effective permissions for the next layer down
 */
function calculateEffectivePermissions(
  higherPermissions: LayerPermissions,
  currentPermissions?: Partial<LayerPermissions>
): LayerPermissions {
  const effective: LayerPermissions = {
    modifiable: [...(currentPermissions?.modifiable ?? [])],
    locked: [...higherPermissions.locked, ...(currentPermissions?.locked ?? [])],
    hidden: [...higherPermissions.hidden, ...(currentPermissions?.hidden ?? [])],
  };

  // Can only modify what higher layer allows AND what current layer specifies
  effective.modifiable = effective.modifiable.filter((path) =>
    higherPermissions.modifiable.includes(path) &&
    !higherPermissions.locked.includes(path)
  );

  return effective;
}

/**
 * Main settings resolution function
 *
 * Merges settings from Portal → Developer → User with permission checks
 */
export function resolveSettings(
  portalSettings: PartialSettings = {},
  developerSettings: PartialSettings = {},
  userSettings: PartialSettings = {},
  context?: SearchContext
): LayeredSettings {
  // Get permissions for each layer
  const portalPermissions: LayerPermissions = {
    ...DEFAULT_PORTAL_PERMISSIONS,
    ...(portalSettings as { permissions?: Partial<LayerPermissions> }).permissions,
  };

  const developerPermissions = calculateEffectivePermissions(
    portalPermissions,
    (developerSettings as { permissions?: Partial<LayerPermissions> }).permissions ?? DEFAULT_DEVELOPER_PERMISSIONS
  );

  // Step 1: Start with system defaults
  let resolved = deepMerge({} as SearchAizSettings, DEFAULT_SETTINGS);

  // Step 2: Apply portal settings (no restrictions on portal)
  resolved = deepMerge(resolved, portalSettings as DeepPartial<SearchAizSettings>);

  // Step 3: Apply developer settings (respecting portal permissions)
  resolved = applyLayerSettings(resolved, developerSettings, portalPermissions);

  // Step 4: Apply user settings (respecting developer permissions)
  resolved = applyLayerSettings(resolved, userSettings, developerPermissions);

  // Step 5: Apply context-specific overrides if context provided
  if (context && resolved.contextOverrides[context]) {
    const contextOverrides = resolved.contextOverrides[context]!;
    resolved = deepMerge(resolved, contextOverrides as DeepPartial<SearchAizSettings>);
  }

  // Step 6: Set final effective permissions
  resolved.permissions = developerPermissions;

  return {
    portal: portalSettings,
    developer: developerSettings,
    user: userSettings,
    resolved,
  };
}

// =============================================================================
// SETTING VALIDATORS
// =============================================================================

/**
 * Validate and constrain a settings value
 */
export function validateSetting(
  path: string,
  value: unknown,
  resolved: SearchAizSettings
): { valid: boolean; value: unknown; error?: string } {
  switch (path) {
    case 'results.perPage':
      const perPage = Number(value);
      if (isNaN(perPage) || perPage < 1) {
        return { valid: false, value, error: 'Must be a positive number' };
      }
      const maxPerPage = resolved.results.maxResults;
      return {
        valid: true,
        value: Math.min(Math.max(1, perPage), maxPerPage),
      };

    case 'ai.rerank.scoreThreshold':
      const threshold = Number(value);
      if (isNaN(threshold) || threshold < 0 || threshold > 1) {
        return { valid: false, value, error: 'Must be between 0 and 1' };
      }
      return { valid: true, value: threshold };

    case 'sources.default':
      if (!Array.isArray(value)) {
        return { valid: false, value, error: 'Must be an array' };
      }
      // Filter to only available sources
      const validSources = (value as string[]).filter((s) =>
        resolved.sources.available.includes(s)
      );
      return { valid: true, value: validSources };

    case 'mode.default':
      if (!resolved.mode.availableModes.includes(value as SearchMode)) {
        return { valid: false, value, error: 'Mode not available' };
      }
      return { valid: true, value };

    default:
      return { valid: true, value };
  }
}

// =============================================================================
// SETTING UPDATE HELPERS
// =============================================================================

/**
 * Check if a user can modify a specific setting
 */
export function canUserModify(
  path: string,
  resolved: SearchAizSettings
): boolean {
  const { permissions } = resolved;

  // Check if locked
  if (permissions.locked.includes(path)) {
    return false;
  }

  // Check if modifiable
  if (!permissions.modifiable.includes(path)) {
    return false;
  }

  // Check special per-setting flags
  switch (path) {
    case 'ai.rerank.enabled':
      return resolved.ai.rerank.userCanDisable;
    case 'ai.analysis.enabled':
      return resolved.ai.analysis.userCanToggle;
    case 'ai.conversation.enabled':
      return resolved.ai.conversation.userCanToggle;
    case 'sources.default':
      return resolved.sources.userCanChange;
    case 'mode.default':
      return resolved.mode.allowUserChange;
    default:
      return true;
  }
}

/**
 * Check if a setting should be hidden from the UI
 */
export function isSettingHidden(
  path: string,
  resolved: SearchAizSettings
): boolean {
  return resolved.permissions.hidden.includes(path);
}

/**
 * Get all user-modifiable settings for building the settings UI
 */
export function getUserModifiableSettings(
  resolved: SearchAizSettings
): string[] {
  return resolved.permissions.modifiable.filter(
    (path) => canUserModify(path, resolved) && !isSettingHidden(path, resolved)
  );
}

// =============================================================================
// STORAGE HELPERS
// =============================================================================

const STORAGE_KEY = 'searchaiz:user-settings';

/**
 * Load user settings from localStorage
 */
export function loadUserSettings(): PartialSettings {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as PartialSettings;
    }
  } catch (error) {
    console.warn('Failed to load SearchAiz user settings:', error);
  }

  return {};
}

/**
 * Save user settings to localStorage
 */
export function saveUserSettings(settings: PartialSettings): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save SearchAiz user settings:', error);
  }
}

/**
 * Clear user settings from localStorage
 */
export function clearUserSettings(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear SearchAiz user settings:', error);
  }
}

/**
 * Update a single user setting
 */
export function updateUserSetting(
  path: string,
  value: unknown,
  currentSettings: PartialSettings
): PartialSettings {
  const newSettings = deepMerge(
    {} as Record<string, unknown>,
    currentSettings as Record<string, unknown>
  ) as PartialSettings;

  setNestedValue(newSettings as Record<string, unknown>, path, value);

  return newSettings;
}
